import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NewSpectraButton } from "./NewSpectraButton";
import SpectraAccordian from "./SpectraAccordian";
import { useGetSpectras } from "../../../api/queryHooks";
import UserAuthStore from "../../../store/userStore";
import UIStore from "../../../store/uiStore";
import spaceStore from "../../../store/spaceStore";

const SpectraSidebarSection = () => {
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { space_id } = useParams();
  const setOpenSpectraId = spaceStore((state) => state.setOpenSpectraId);
  const isCollapsed = UIStore((state) => state.sidebarCollapsed.spectra);
  const toggleSection = UIStore((state) => state.toggleSidebarSection);
  const query = UIStore((state) => state.sidebarQuery);

  // Use React Query for data fetching - read directly without Zustand sync
  const { data: spaces, isLoading: loading } = useGetSpectras(orgId, user?.uid, {
    enabled: !!orgId && !!user?.uid,
  });

  // Filter by the sidebar search query (case-insensitive on title).
  const filteredSpaces = useMemo(() => {
    if (!Array.isArray(spaces)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return spaces;
    return spaces.filter((s) => (s.title || "").toLowerCase().includes(q));
  }, [spaces, query]);

  // Split into pinned / unpinned groups for rendering
  const pinnedSpaces = useMemo(
    () => filteredSpaces.filter((s) => s.pinned),
    [filteredSpaces]
  );
  const unpinnedSpaces = useMemo(
    () => filteredSpaces.filter((s) => !s.pinned),
    [filteredSpaces]
  );

  // While searching, force the section open so results are visible.
  const isOpen = !!query || !isCollapsed;

  // Auto-open the accordion for the active space when the page loads or
  // the user navigates directly to a spectra URL (e.g. after a hard refresh).
  // Tracked via a ref (not read from state) so this only fires once per
  // distinct space_id navigation — previously openSpectraId was a dependency,
  // so manually collapsing the accordion (setOpenSpectraId(null)) re-ran this
  // effect immediately and forced it back open, making it impossible to fold
  // the arrow while viewing a chat inside that same spectra.
  const autoOpenedSpaceIdRef = useRef(null);
  useEffect(() => {
    if (!space_id || !spaces?.length) return;
    if (autoOpenedSpaceIdRef.current === space_id) return;
    const match = spaces.find((s) => s.space_id === space_id);
    if (match) {
      autoOpenedSpaceIdRef.current = space_id;
      setOpenSpectraId(space_id);
    }
  }, [space_id, spaces, setOpenSpectraId]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className="flex-1 min-h-0 overflow-y-auto show-scrollbar-on-hover"
        id="spectras-container"
        style={{ overflowY: "scroll" }}
      >
        <div
          id="spectra-section-header"
          className="sticky top-0 z-10 bg-surface-sidebar flex items-center"
        >
          <button
            type="button"
            onClick={() => toggleSection("spectra")}
            className="pl-2 pr-1 text-ink-control-border dark:text-ink-white"
            aria-label={isCollapsed ? "Expand spectra" : "Collapse spectra"}
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <NewSpectraButton />
          </div>
        </div>
        {isOpen ? (
          <div className="px-[12px] space-y-0">
            {loading ? (
              <div className="text-center text-[13px] dark:text-ink-hex-aaaaaa text-ink-copy-hover px-[12px]">
                Loading...
              </div>
            ) : filteredSpaces.length === 0 ? (
              <div className="text-center text-[12px] italic dark:text-ink-gray-400 text-ink-gray-500 py-2">
                {query ? "No spectra match your search" : "No spectra created"}
              </div>
            ) : (
              <>
                {pinnedSpaces.length > 0 && (
                  <>
                    <div className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500 select-none">
                      Pinned
                    </div>
                    {pinnedSpaces.map((space) => (
                      <SpectraAccordian key={space.space_id} space={space} />
                    ))}
                    {unpinnedSpaces.length > 0 && (
                      <div className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500 select-none">
                        All
                      </div>
                    )}
                  </>
                )}
                {unpinnedSpaces.map((space) => (
                  <SpectraAccordian key={space.space_id} space={space} />
                ))}
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SpectraSidebarSection;
